import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import ModuleHeaderStrip from '../../../components/common/ModuleHeaderStrip';
import DynamicForm from '../../../components/dynamic/DynamicForm';
import DataTable from '../../../components/common/DataTable';
import Pagination from '../../../components/common/Pagination';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const DEFAULT_CLASSES = [
  'Pre-Primary (Nursery)', 'Pre-Primary (LKG)', 'Pre-Primary (UKG)',
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
  'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
  'Class 11 (Science)', 'Class 11 (Commerce)', 'Class 11 (Arts)',
  'Class 12 (Science)', 'Class 12 (Commerce)', 'Class 12 (Arts)',
];
const SECTIONS = ['A', 'B', 'C', 'D', 'E'];

export default function StudentsPage() {
  const [fields, setFields] = useState([]);
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [classOptions, setClassOptions] = useState(DEFAULT_CLASSES);
  const [sectionOptions, setSectionOptions] = useState(['A', 'B', 'C', 'D', 'E']);

  // Load school config for class options, then patch fields
  useEffect(() => {
    api.get('/records/school_config', { params: { limit: 1 } })
      .then(r => {
        const rec = r.data?.records?.[0]?.recordData || r.data?.record?.recordData;
        if (rec?.classes?.length) setClassOptions(rec.classes);
        if (rec?.sections?.length) setSectionOptions(rec.sections);
      }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/modules/student_management/fields')
      .then(r => {
        const raw = r.data.data || r.data.records || [];
        // Override class_grade and section to use dropdown options
        const patched = raw.map(f => {
          if (f.fieldKey === 'class_grade') {
            return { ...f, fieldType: 'select', options: classOptions.map(c => ({ label: c, value: c })) };
          }
          if (f.fieldKey === 'section') {
            return { ...f, fieldType: 'select', options: sectionOptions.map(s => ({ label: `Section ${s}`, value: s })) };
          }
          return f;
        });
        setFields(patched);
      }).catch(() => {});
  }, [classOptions, sectionOptions]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: 20, search };
    if (filterClass) params.class_grade = filterClass;
    const r = await api.get('/education/students', { params }).catch(() => ({ data: { records: [], total: 0 } }));
    setStudents(r.data.records || []);
    setTotal(r.data.total || 0);
    setLoading(false);
  }, [page, search, filterClass]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/education/students/${editing._id}`, data);
      else await api.post('/education/students', data);
      setToast({ show: true, message: `Student ${editing ? 'updated' : 'added'}!`, type: 'success' });
      setShowForm(false); setEditing(null); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error', type: 'danger' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this student?')) return;
    await api.delete(`/education/students/${id}`);
    setToast({ show: true, message: 'Student removed', type: 'success' }); load();
  };

  const statusColors = { active: '#198754', inactive: '#dc3545', graduated: '#0d6efd', transferred: '#fd7e14' };

  // NOTE: every column reads from row.recordData via its render fn, but each
  // column's `key` must be unique — DataTable uses it as the React key for
  // <th>/<td>. Make them unique per column to avoid duplicate-key warnings.
  const columns = [
    { key: 'admission_no', label: 'Admission No.', render: (_, row) => <code style={{ fontSize: 11 }}>{row.recordData?.admission_number || '—'}</code> },
    { key: 'student_name', label: 'Student Name', render: (_, row) => <span className="fw-medium">{row.recordData?.first_name} {row.recordData?.last_name}</span> },
    { key: 'class',        label: 'Class',        render: (_, row) => row.recordData?.class_grade ? <span className="badge bg-light text-dark">{row.recordData.class_grade} {row.recordData.section && `- ${row.recordData.section}`}</span> : '—' },
    { key: 'parent',       label: 'Parent',       render: (_, row) => row.recordData?.parent_name || '—' },
    { key: 'phone',        label: 'Phone',        render: (_, row) => <span style={{ fontSize: 12 }}>{row.recordData?.parent_phone || '—'}</span> },
    { key: 'status',       label: 'Status',       render: (_, row) => {
      const v = row.recordData;
      const s = v?.status || 'active';
      return <span className="badge" style={{ background: `${statusColors[s]}20`, color: statusColors[s] }}>{s}</span>;
    }},
    { key: '_id', label: 'Actions', render: (v, row) => (
      <div className="d-flex gap-1">
        <button className="btn btn-outline-primary btn-sm" onClick={e => { e.stopPropagation(); setEditing({ ...row.recordData, _id: row._id }); setShowForm(true); }}><i className="bi bi-pencil"></i></button>
        <button className="btn btn-outline-danger btn-sm" onClick={e => { e.stopPropagation(); handleDelete(v); }}><i className="bi bi-trash"></i></button>
      </div>
    )},
  ];

  return (
    <div>
      <PageHeader title="Student Management" icon="bi-person-badge-fill"
        subtitle="Manage all student records"
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <input type="text" className="form-control form-control-sm" placeholder="Search students..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ width: 200 }} />
            <select className="form-select form-select-sm" style={{ width: 180 }} value={filterClass} onChange={e => { setFilterClass(e.target.value); setPage(1); }}>
              <option value="">All Classes</option>
              {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setShowForm(true); }}><i className="bi bi-person-plus me-1"></i>Add Student</button>
          </div>
        }
      />

      <ModuleHeaderStrip
        icon="bi-person-badge-fill"
        label="Students"
        chips={[
          { label: 'Total students', value: total },
          { label: 'Active classes', value: classOptions.length },
          { label: 'Sections',       value: sectionOptions.length },
        ]}
        variant="info"
      />

      {showForm && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between">
            <span>{editing ? 'Edit Student' : 'Admit New Student'}</span>
            <button className="btn-close" onClick={() => { setShowForm(false); setEditing(null); }}></button>
          </div>
          <div className="card-body">
            <DynamicForm fields={fields} initialData={editing || {}} onSubmit={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} loading={saving} submitLabel={editing ? 'Update Student' : 'Admit Student'} />
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body p-0">
          <DataTable columns={columns} data={students} loading={loading} emptyTitle="No students found" emptyIcon="bi-person-badge-fill" emptyAction={<button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}><i className="bi bi-plus-lg me-1"></i>Add First Student</button>} />
        </div>
      </div>
      <Pagination page={page} totalPages={Math.ceil(total / 20)} onPageChange={setPage} total={total} limit={20} />
      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
